import React, { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../common/breadcrumb";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Table,
  Label,
  FormGroup,
  Badge,
} from "reactstrap";
import {
  getSupportUsers,
  addSupportUser,
  removeSupportUser,
  getUserListing,
} from "../../utils/apiCalls";
import { errorToast } from "../../shared/Toast";
import { useSelector } from "react-redux";

const SupportUsers = () => {
  const userType = localStorage.getItem("userType");
  const loggedInMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchantId = userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;

  const [supportUsers, setSupportUsers] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  // Add modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Delete confirm modal
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchSupportUsers = async (merchantId) => {
    if (!merchantId) return;
    setIsFetching(true);
    const data = await getSupportUsers(merchantId);
    setSupportUsers(data || []);
    setIsFetching(false);
  };

  useEffect(() => {
    if (selectedMerchantId) {
      fetchSupportUsers(selectedMerchantId);
    } else {
      setSupportUsers([]);
    }
  }, [selectedMerchantId]);

  const handleSearchUsers = async () => {
    if (!searchInput.trim()) {
      errorToast("Please enter a search term");
      return;
    }
    setIsSearching(true);
    const data = await getUserListing({ search: searchInput, limit: 50 });
    setAllUsers(data.users || []);
    setIsSearching(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchUsers();
    }
  };

  const handleAddSupportUser = async () => {
    if (!selectedUserId) {
      errorToast("Please select a user");
      return;
    }
    const result = await addSupportUser({
      userId: parseInt(selectedUserId),
      merchantId: parseInt(selectedMerchantId),
    });
    if (result) {
      setAddModalOpen(false);
      setSearchInput("");
      setAllUsers([]);
      setSelectedUserId("");
      fetchSupportUsers(selectedMerchantId);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const result = await removeSupportUser(deleteTarget.id);
    if (result) {
      setDeleteModal(false);
      setDeleteTarget(null);
      fetchSupportUsers(selectedMerchantId);
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Support Users" parent="Merchants" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">
                    Support Users {supportUsers.length > 0 && `(${supportUsers.length})`}
                  </h5>
                  {selectedMerchantId && (
                    <Button color="primary" onClick={() => {
                      setAllUsers([]);
                      setSearchInput("");
                      setSelectedUserId("");
                      setAddModalOpen(true);
                    }}>
                      Add Support User
                    </Button>
                  )}
                </div>

                {!selectedMerchantId ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <p>Please select a merchant from the header to view support users.</p>
                  </div>
                ) : isFetching ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <p>Loading support users...</p>
                  </div>
                ) : (
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>User Type</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supportUsers.length > 0 ? (
                        supportUsers.map((su) => (
                          <tr key={su.id} style={{ verticalAlign: "middle" }}>
                            <td>{su.User?.id}</td>
                            <td>{su.User?.firstName} {su.User?.lastName}</td>
                            <td>{su.User?.email || "-"}</td>
                            <td>{su.User?.phoneNo || "-"}</td>
                            <td>{su.User?.userType || "-"}</td>
                            <td>
                              <Badge color={su.User?.active ? "success" : "secondary"}>
                                {su.User?.active ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td>
                              <i
                                className="fa fa-trash"
                                style={{ fontSize: 18, cursor: "pointer", color: "rgb(220, 53, 69)" }}
                                title="Remove"
                                onClick={() => {
                                  setDeleteTarget(su);
                                  setDeleteModal(true);
                                }}
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No support users assigned to this merchant.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Support User Modal */}
      <Modal isOpen={addModalOpen} toggle={() => setAddModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setAddModalOpen(false)}>
          Add Support User
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Search Users</Label>
            <div className="d-flex gap-2">
              <Input
                type="text"
                placeholder="Search by name, email, phone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              <Button color="primary" onClick={handleSearchUsers} disabled={isSearching} style={{ whiteSpace: "nowrap" }}>
                {isSearching ? "Searching..." : "Search"}
              </Button>
            </div>
          </FormGroup>
          {allUsers.length > 0 && (
            <FormGroup>
              <Label>Select User</Label>
              <Input
                type="select"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="">-- Select User --</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} - {u.email || u.phoneNo || ""} ({u.userType})
                  </option>
                ))}
              </Input>
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddSupportUser} disabled={!selectedUserId}>
            Add
          </Button>
          <Button color="secondary" onClick={() => setAddModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>Confirm Remove</ModalHeader>
        <ModalBody>
          Are you sure you want to remove{" "}
          <strong>{deleteTarget?.User?.firstName} {deleteTarget?.User?.lastName}</strong>{" "}
          as a support user?
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDeleteConfirm}>
            Remove
          </Button>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default SupportUsers;
